import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from '../api/axios';
import './Register.css';

const PLANS = [
  {
    id: 'FREE',
    name: 'Free',
    price: 'Free',
    userLimit: 5,
    features: ['Up to 5 users', 'All modules included', 'All features', 'Community support'],
    trial: true,
  },
  {
    id: 'SILVER',
    name: 'Silver',
    price: 'Contact us',
    userLimit: 10,
    features: ['Up to 10 users', 'All modules included', 'All features', 'Email support'],
    trial: false,
  },
  {
    id: 'GOLD',
    name: 'Gold',
    price: 'Contact us',
    userLimit: 25,
    features: ['Up to 25 users', 'All modules included', 'All features', 'Priority support'],
    trial: false,
    popular: true,
  },
  {
    id: 'PLATINUM',
    name: 'Platinum',
    price: 'Contact us',
    userLimit: 50,
    features: ['Up to 50 users', 'All modules included', 'All features', 'Dedicated support + SLA'],
    trial: false,
  },
];

function Register() {
  const [step, setStep] = useState(1); // 1 = details, 2 = plan
  const [form, setForm] = useState({
    companyName: '',
    industry: '',
    country: 'India',
    adminName: '',
    email: '',
    password: '',
    confirmPassword: '',
    alternatePhone: '',
    careOf: '',
    secondContactName: '',
    secondContact: '',
  });
  const [countryCode, setCountryCode] = useState('91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('FREE');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (error && cardRef.current) {
      cardRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  const handleChange = (e) => {
    let val = e.target.value;
    if (['phone', 'alternatePhone', 'secondContact'].includes(e.target.name)) {
      val = val.replace(/[a-zA-Z]/g, '');
    }
    setForm({ ...form, [e.target.name]: val });
    setError('');
  };

  const handleNext = (e) => {
    e.preventDefault();
    const combinedPhone = `${countryCode}${phoneNumber}`;
    if (!/^[0-9+\-\s()]{7,20}$/.test(combinedPhone)) {
      setError('Invalid phone number. It must be between 7 and 20 digits.');
      return;
    }


    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const password = form.password;
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/[0-9]/.test(password) || !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      setError('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character.');
      return;
    }

    setStep(2);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post('/public/register', {
        companyName: form.companyName,
        industry: form.industry,
        country: form.country,
        adminName: form.adminName,
        email: form.email,
        password: form.password,
        phone: `${countryCode}${phoneNumber}`,
        alternatePhone: form.alternatePhone,
        careOf: form.careOf,
        secondContactName: form.secondContactName,
        secondContact: form.secondContact,
        planTier: selectedPlan,
      });
      setSuccess(true);
    } catch (err) {
      console.error('Registration error:', err.response?.data);
      const status = err.response?.status;
      const errorData = err.response?.data;
      
      // Extract message from various possible error structures
      let serverMsg = '';
      if (typeof errorData === 'string') {
        serverMsg = errorData;
      } else if (errorData?.message) {
        serverMsg = typeof errorData.message === 'string' ? errorData.message : JSON.stringify(errorData.message);
      } else if (errorData?.error?.message) {
        serverMsg = typeof errorData.error.message === 'string' ? errorData.error.message : JSON.stringify(errorData.error.message);
      } else if (errorData?.error && typeof errorData.error === 'string') {
        serverMsg = errorData.error;
      } else if (errorData?.error && typeof errorData.error === 'object') {
        serverMsg = JSON.stringify(errorData.error);
      }

      if (status === 409) {
        setError(`An account with ${form.email} already exists. Try logging in instead.`);
        setStep(1);
      } else if (status === 400) {
        setError(serverMsg || 'Please check your details and try again.');
        setStep(1);
      } else if (status === 500) {
        setError('Something went wrong on our end. Please try again in a moment.');
      } else if (!err.response) {
        setError('Cannot reach the server. Please check your connection.');
      } else {
        setError(serverMsg || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="register-root">
        <div className="register-card">
          <div className="register-success-icon">✓</div>
          <h2>Registration Successful!</h2>
          <p>We've sent a verification email to <strong>{form.email}</strong>.</p>
          <p>Please check your inbox and click the verification link to activate your account.</p>
          <Link to="/login" className="register-login-link">Back to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="register-root">
      <div className="register-card" ref={cardRef}>
        {/* Brand logo */}
        <div className="register-card-brand">
          <div className="register-card-logo">🏗</div>
          <span className="register-card-brand-name">Construction ERP</span>
        </div>

        <div className="register-card-header">
          <h1 className="register-title">Create your account</h1>
          <p className="register-subtitle">Start your free trial today</p>
        </div>

        {error && <div className="register-error" role="alert">{error}</div>}

        {step === 1 && (
          <form onSubmit={handleNext} className="register-form">
            <div className="register-field">
              <label htmlFor="companyName">Company Name *</label>
              <input id="companyName" name="companyName" type="text" required
                value={form.companyName} onChange={handleChange} placeholder="Acme Construction" />
            </div>
            <div className="register-row">
              <div className="register-field">
                <label htmlFor="industry">Industry *</label>
                <input id="industry" name="industry" type="text" required
                  value={form.industry} onChange={handleChange} placeholder="Construction" />
              </div>
              <div className="register-field">
                <label htmlFor="country">Country *</label>
                <select
                  id="country"
                  name="country"
                  required
                  value={form.country}
                  onChange={handleChange}
                >
                  <option value="">Select Country</option>
                  <option value="India">India</option>
                  <option value="United States">United States</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Canada">Canada</option>
                  <option value="Australia">Australia</option>
                  <option value="Singapore">Singapore</option>
                  <option value="United Arab Emirates">United Arab Emirates</option>
                </select>
              </div>
            </div>
            <div className="register-field">
              <label htmlFor="adminName">Your Name *</label>
              <input id="adminName" name="adminName" type="text" required
                value={form.adminName} onChange={handleChange} placeholder="John Doe" />
            </div>
            <div className="register-row">
              <div className="register-field">
                <label htmlFor="phone">Phone Number *</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    placeholder="Code"
                    value={countryCode}
                    onChange={(e) => {
                      setCountryCode(e.target.value.replace(/[^0-9+]/g, ''));
                      setError('');
                    }}
                    style={{ width: '80px', flexShrink: 0 }}
                    maxLength="5"
                    required
                  />
                  <input
                    type="tel"
                    placeholder="9876543210"
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''));
                      setError('');
                    }}
                    style={{ flexGrow: 1 }}
                    maxLength="15"
                    required
                  />
                </div>
              </div>
              <div className="register-field">
                <label htmlFor="alternatePhone">Alternate Phone</label>
                <input id="alternatePhone" name="alternatePhone" type="tel"
                  value={form.alternatePhone} onChange={handleChange} placeholder="9876543211" />
              </div>
            </div>
            <div className="register-field">
              <label htmlFor="careOf">Care Of (C/O)</label>
              <input id="careOf" name="careOf" type="text"
                value={form.careOf} onChange={handleChange} placeholder="c/o Contact Person Name" />
            </div>
            <div className="register-row">
              <div className="register-field">
                <label htmlFor="secondContactName">Second Contact Name *</label>
                <input id="secondContactName" name="secondContactName" type="text" required
                  value={form.secondContactName} onChange={handleChange} placeholder="Secondary Contact Person" />
              </div>
              <div className="register-field">
                <label htmlFor="secondContact">Second Contact Number *</label>
                <input id="secondContact" name="secondContact" type="tel" required
                  value={form.secondContact} onChange={handleChange} placeholder="9876543212" />
              </div>
            </div>
            <div className="register-field">
              <label htmlFor="email">Email *</label>
              <input id="email" name="email" type="email" required
                value={form.email} onChange={handleChange} placeholder="you@company.com" />
            </div>
            <div className="register-row">
              <div className="register-field">
                <label htmlFor="password">Password *</label>
                <input id="password" name="password" type="password" required minLength={8}
                  value={form.password} onChange={handleChange} placeholder="••••••••" />
              </div>
              <div className="register-field">
                <label htmlFor="confirmPassword">Confirm Password *</label>
                <input id="confirmPassword" name="confirmPassword" type="password" required
                  value={form.confirmPassword} onChange={handleChange} placeholder="••••••••" />
              </div>
            </div>
            <button type="submit" className="register-btn">Next: Choose Plan →</button>
            <p className="register-login-hint">Already have an account? <Link to="/login">Sign in</Link></p>
          </form>
        )}

        {step === 2 && (
          <div className="register-plans">
            <h2 className="register-plans-title">Choose your plan</h2>
            <div className="plans-grid">
              {PLANS.map(plan => (
                <div
                  key={plan.id}
                  className={`plan-card ${selectedPlan === plan.id ? 'plan-card--selected' : ''} ${plan.popular ? 'plan-card--popular' : ''}`}
                  onClick={() => setSelectedPlan(plan.id)}
                  role="radio"
                  aria-checked={selectedPlan === plan.id}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setSelectedPlan(plan.id)}
                >
                  {plan.popular && <span className="plan-badge">Popular</span>}
                  <h3 className="plan-name">{plan.name}</h3>
                  <p className="plan-price">{plan.price}</p>
                  {plan.trial && <p className="plan-trial">⏱ 1 month free trial</p>}
                  {!plan.trial && <p className="plan-trial">💳 Paid plan</p>}
                  <ul className="plan-features">
                    {plan.features.map(f => <li key={f}>✓ {f}</li>)}
                  </ul>
                </div>
              ))}
            </div>
            <div className="register-plan-actions">
              <button type="button" className="register-btn-secondary" onClick={() => setStep(1)}>← Back</button>
              <button type="button" className="register-btn" onClick={handleSubmit} disabled={loading}>
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Register;
